import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { useLanguage } from "../../../../context/LanguageContext";
import { useAdminProductsTranslation } from "../translations";
import { getAdminStoreProducts, deleteProduct, updateProduct } from "../../../../firebase/product-functions";
import { getStoreCategories } from "../../../../firebase/category-functions";
import { addToSales, removeFromSales, getSalesItems } from "../../../../firebase/sales-functions";
import { DEFAULT_STORE_ID } from '../../../../firebase/firebase';

import ProductsHeader from "./components/ProductsHeader";
import ProductsActionBar from "./components/ProductsActionBar";
import ProductsTable from "./components/ProductsTable";
import DiscountModal from "./components/DiscountModal";
// import DiscountManagementModal from "./components/DiscountManagementModal/DiscountManagementModal";
import LoadingSpinner from "./components/LoadingSpinner";


import { useProductsLogic } from "./hooks/useProductsLogic";
import { useDiscountLogic } from "./hooks/useDiscountLogic";
import { useBulkActions } from "./hooks/useBulkActions";
import "./AdminProductsPage.css";

const AdminProducts = () => {
    const navigate = useNavigate();
    const { language } = useLanguage();
    const { t } = useAdminProductsTranslation(language);

    // Main state
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [salesItems, setSalesItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    // Filter and search state
    const [search, setSearch] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("All");
    const [statusFilter, setStatusFilter] = useState("All");
    const [selectedProducts, setSelectedProducts] = useState([]);

    // Discount management state
    const [showManagementModal, setShowManagementModal] = useState(false);
    const [managementProduct, setManagementProduct] = useState(null);
    const [managementDiscounts, setManagementDiscounts] = useState([]);

    // Custom hooks for logic separation
    const {
        filteredProducts,
        handleSelectAll,
        handleSelectProduct,
        getProductDiscount,
        getProductDiscounts,
        calculateDiscountedPrice,

    } = useProductsLogic({
        products,
        salesItems,
        search,
        categoryFilter,
        statusFilter,
        selectedProducts,
        setSelectedProducts
    });

    const {
        showDiscountModal,
        setShowDiscountModal,
        selectedProductForDiscount,
        setSelectedProductForDiscount,
        discountData,
        setDiscountData,
        handleAddDiscount,
        handleDiscountSubmit,
        handleRemoveDiscount
    } = useDiscountLogic({
        products,
        salesItems,
        setSalesItems,
        selectedProducts,
        setSelectedProducts,
        getProductDiscount,
        calculateDiscountedPrice,
        setLoading,
        setError
    });

    const { handleBulkAction } = useBulkActions({
        products,
        setProducts,
        selectedProducts,
        setSelectedProducts,
        salesItems,
        getProductDiscount,
        setLoading,
        setError,
        setSelectedProductForDiscount,
        setShowDiscountModal
    });

    // Fetch initial data
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError("");

            try {
                // Fetch products
                const { products: productList } = await getAdminStoreProducts(DEFAULT_STORE_ID, {
                    limit: 100
                });
                setProducts(productList);

                // Fetch categories for filter
                const categoriesList = await getStoreCategories(DEFAULT_STORE_ID);
                setCategories(categoriesList);

                // Fetch sales items to check which products have discounts
                const salesList = await getSalesItems(DEFAULT_STORE_ID);
                setSalesItems(salesList);

            } catch (err) {
                console.error("Error fetching products:", err);
                setError(t('failedToLoadProducts'));
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    // Handle delete product
    const handleDeleteProduct = async (productId, productName) => {
        if (window.confirm(t('deleteConfirmation', { productName }))) {
            try {
                setLoading(true);

                // Delete from Firebase
                await deleteProduct(DEFAULT_STORE_ID, productId);

                // Remove from sales if exists
                const productDiscounts = getProductDiscounts(productId);
                for (const discount of productDiscounts) {
                    await removeFromSales(DEFAULT_STORE_ID, discount.id);
                }

                // Update local state
                setProducts(products.filter(p => p.id !== productId));
                setSalesItems(salesItems.filter(sale => sale.productId !== productId));
            } catch (error) {
                console.error("Error deleting product:", error);
                setError(t('failedToDeleteProduct'));
            } finally {
                setLoading(false);
            }
        }
    };

    // Handle direct discount removal by ID (simplified approach)
    const handleDirectRemoveDiscount = async (discountId) => {
        try {
            setLoading(true);

            // Call the Firebase function to remove the discount
            await removeFromSales(DEFAULT_STORE_ID, discountId);

            // Update local state immediately
            setSalesItems(prevSales => prevSales.filter(sale => sale.id !== discountId));

        } catch (error) {
            console.error("Error removing discount:", error);
            setError(t('failedToRemoveDiscount'));
        } finally {
            setLoading(false);
        }
    };    // Handle manage discounts
    const handleManageDiscounts = (product, productDiscounts) => {
        setManagementProduct(product);
        setManagementDiscounts(productDiscounts);
        setShowManagementModal(true);
    };

    // Handle remove individual discount
    const handleRemoveIndividualDiscount = async (discountId) => {
        try {
            setLoading(true);
            await removeFromSales(DEFAULT_STORE_ID, discountId);

            // Update local state
            setSalesItems(salesItems.filter(sale => sale.id !== discountId));

            // Update management modal state
            setManagementDiscounts(managementDiscounts.filter(d => d.id !== discountId));

            // Close management modal if no discounts left
            if (managementDiscounts.length <= 1) {
                setShowManagementModal(false);
            }
        } catch (error) {
            console.error("Error removing discount:", error);
            setError(t('failedToRemoveDiscount'));
        } finally {
            setLoading(false);
        }
    };

    // Get unique categories for the filter
    const uniqueCategories = ["All", ...new Set(products.map(product => product.category).filter(Boolean))];
    const allCategories = categories.length > 0
        ? ["All", ...categories.map(cat => cat.name)]
        : uniqueCategories;

    // Note: Product limits are enforced server-side in Cloud Functions
    // The createProduct function will return 'resource-exhausted' error if limit reached
    const handleAddProduct = () => {
        navigate("/admin/products/new");
    };

    if (loading) {
        return <LoadingSpinner message={t('loadingProducts')} />;
    }

    return (
        <div className="admin-products">
            <ProductsHeader />

            {/* {error && <ErrorMessage message={error} />} */}

            <ProductsActionBar
                onAddProduct={handleAddProduct}
                onViewSales={() => navigate("/admin/sales")}
                activeSalesCount={salesItems.filter(sale => sale.isActive).length}
                onBulkAction={handleBulkAction}
                selectedCount={selectedProducts.length}
                search={search}
                setSearch={setSearch}
                categoryFilter={categoryFilter}
                setCategoryFilter={setCategoryFilter}
                statusFilter={statusFilter}
                setStatusFilter={setStatusFilter}
                allCategories={allCategories}
            />

            <ProductsTable
                products={filteredProducts}
                selectedProducts={selectedProducts}
                onSelectAll={handleSelectAll}
                onSelectProduct={handleSelectProduct}
                onEdit={(productId) => navigate(`/admin/products/edit/${productId}`)}
                onDelete={handleDeleteProduct}
                onAddDiscount={handleAddDiscount}
                onRemoveDiscount={handleRemoveDiscount}
                onDirectRemoveDiscount={handleDirectRemoveDiscount}
                onManageDiscounts={handleManageDiscounts}
                getProductDiscount={getProductDiscount}
                getProductDiscounts={getProductDiscounts}
                calculateDiscountedPrice={calculateDiscountedPrice}
                salesItems={salesItems} // Pass salesItems for debugging
            />

            {/* <Pagination /> */}

            {showDiscountModal && (
                <DiscountModal
                    selectedProduct={selectedProductForDiscount}
                    selectedCount={selectedProducts.length}
                    discountData={discountData}
                    setDiscountData={setDiscountData}
                    onSubmit={handleDiscountSubmit}
                    onClose={() => setShowDiscountModal(false)}
                    calculateDiscountedPrice={calculateDiscountedPrice}
                />
            )}

            {/* {showManagementModal && (
                <DiscountManagementModal
                    product={managementProduct}
                    productDiscounts={managementDiscounts}
                    onClose={() => setShowManagementModal(false)}
                    onRemoveDiscount={handleRemoveIndividualDiscount}
                    onAddDiscount={(product) => {
                        setShowManagementModal(false);
                        handleAddDiscount(product);
                    }}
                    calculateDiscountedPrice={calculateDiscountedPrice}
                />
            )} */}
        </div>
    );
};

export default AdminProducts;